if (!customElements.get('chase-card-gallery')) {
  customElements.define('chase-card-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.media = this.parentElement;
      this.original = this.media.querySelector(':scope > .media');
      this.primary = this.original?.querySelector('img');
      this.track = this.querySelector('[data-gallery-track]');
      this.template = this.querySelector('[data-gallery-template]');
      if (!this.primary || !this.track || !this.template) return;
      this.controller = new AbortController();
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.position = 0;
      this.lockedUntil = 0;
      const signal = this.controller.signal;
      this.mobile.addEventListener('change', () => this.configure(), { signal });
      this.track.addEventListener('scroll', () => {
        this.lockedUntil = Date.now() + 180;
        this.schedule();
      }, { passive: true, signal });
      this.track.addEventListener('pointerdown', event => {
        this.start = { x: event.clientX, y: event.clientY };
        this.loadNearby(this.position);
      }, { passive: true, signal });
      this.track.addEventListener('pointermove', event => {
        if (this.start && Math.hypot(event.clientX - this.start.x, event.clientY - this.start.y) > 8) this.lockedUntil = Date.now() + 400;
      }, { passive: true, signal });
      this.track.addEventListener('pointerup', event => {
        if (this.start && Math.hypot(event.clientX - this.start.x, event.clientY - this.start.y) > 8) this.lockedUntil = Date.now() + 400;
        this.start = null;
      }, { passive: true, signal });
      this.track.addEventListener('pointercancel', () => { this.start = null; this.lockedUntil = Date.now() + 400; }, { passive: true, signal });
      this.track.addEventListener('click', event => {
        if (event.detail !== 0 && Date.now() < this.lockedUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });
      this.track.addEventListener('keydown', event => {
        if (!this.ready || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const next = Math.min(this.slides.length - 1, Math.max(0, this.position + (event.key === 'ArrowRight' ? 1 : -1)));
        this.loadNearby(next);
        this.track.scrollTo({ left: next * this.track.clientWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal });
      this.configure();
    }
    configure() {
      this.observer?.disconnect();
      if (!this.mobile.matches) { this.restore(); return; }
      if (this.ready) return;
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.observer.disconnect();
            this.enhance();
          }
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this.media);
      } else this.enhance();
    }
    enhance() {
      if (this.ready || !this.isConnected || !this.mobile.matches) return;
      this.track.append(this.template.content.cloneNode(true));
      this.track.querySelector('[data-gallery-primary]').replaceWith(this.primary);
      this.slides = Array.from(this.track.querySelectorAll('.chase-card-gallery__slide'));
      this.ready = true;
      this.dataset.ready = '';
      this.media.dataset.chaseGalleryActive = '';
      this.original.setAttribute('aria-hidden', 'true');
      this.position = 0;
      this.track.scrollLeft = 0;
      this.loadNearby(0);
      this.update();
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => {
          if (!this.ready) return;
          this.track.scrollTo({ left: this.position * this.track.clientWidth, behavior: 'instant' });
          this.loadNearby(this.position);
        });
        this.resize.observe(this.track);
      }
    }
    loadNearby(index) {
      if (!this.slides) return;
      const width = Math.ceil(this.track.getBoundingClientRect().width);
      for (let i = Math.max(0, index - 1); i <= Math.min(this.slides.length - 1, index + 1); i++) {
        const image = this.slides[i].querySelector('img');
        if (!image) continue;
        if (image !== this.primary && width) image.sizes = width + 'px';
        if (!image.dataset.gallerySrc) continue;
        image.loading = 'eager';
        image.srcset = image.dataset.gallerySrcset;
        image.src = image.dataset.gallerySrc;
        delete image.dataset.gallerySrc;
        delete image.dataset.gallerySrcset;
      }
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    }
    update() {
      if (!this.ready || !this.track.clientWidth) return;
      const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth)));
      this.position = index;
      this.loadNearby(index);
      this.querySelector('[data-gallery-current]').textContent = String(index + 1);
      this.slides.forEach((slide, i) => { slide.tabIndex = i === index ? 0 : -1; });
      this.querySelector('[data-gallery-status]').textContent = this.dataset.positionLabel.replace('[number]', String(index + 1));
    }
    restore() {
      this.resize?.disconnect();
      if (!this.ready) return;
      this.original.prepend(this.primary);
      this.original.removeAttribute('aria-hidden');
      this.track.replaceChildren();
      delete this.dataset.ready;
      delete this.media.dataset.chaseGalleryActive;
      this.ready = false;
      this.slides = null;
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.restore();
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  });
}
